import type { KeyboardEvent, PointerEvent, ReactNode } from 'react';
import { Feedback } from '@dnd-kit/dom';
import { SortableKeyboardPlugin } from '@dnd-kit/dom/sortable';
import { useSortable } from '@dnd-kit/react/sortable';
import { ArrowDown, ArrowUp, ArrowUpDown, GripVertical } from 'lucide-react';
import { cn } from '../../utils';
import { FilterPopover } from './data-table-filters';
import type {
  DataTableFilterDefinition,
  DataTableFilterLabels,
  DataTableFilterState,
  DataTableFilterValue,
  DataTableSortDirection,
} from './data-table-filters';

/**
 * `feedback: 'clone'` leaves the `<th>` in the header row and sends a copy after
 * the pointer. The default (`'move'`) lifts the cell itself into a fixed overlay
 * and leaves a placeholder, which collapses the header row and desyncs it from
 * the `<colgroup>` the body is sized by.
 *
 * `'none'` looks like the tidier answer and is not: dnd-kit detects collisions
 * from the moving element's shape, so with nothing moving no drop target is ever
 * found and neither the pointer nor the keyboard can complete a drag.
 *
 * Optimistic sorting is dropped — it would slide header cells across columns
 * whose bodies stay put; the drop indicator says where the column will land
 * instead. Replacing the defaults means naming `SortableKeyboardPlugin`
 * explicitly: without it the grip has no keyboard path.
 */
const SORTABLE_PLUGINS = [
  SortableKeyboardPlugin,
  Feedback.configure({ feedback: 'clone' }),
];

export interface DataTableLabels {
  /** Accessible name of the drag grip, e.g. "Reorder column City". */
  reorderColumn: (column: string) => string;
  /** Accessible name of the resize handle. */
  resizeColumn: (column: string) => string;
  resetColumns: string;
}

/** The header-facing half of `DataTableColumn`, so both can render this cell. */
export interface DataTableHeaderColumn<K extends string = string> {
  id: string;
  label: ReactNode;
  labelText?: string;
  /** `false` lets rich header content keep its intrinsic width without clipping. */
  truncate?: boolean;
  sortKey?: K;
  align?: 'left' | 'right' | 'center';
  filter?: DataTableFilterDefinition;
  pinned?: 'start' | 'end';
  resizable?: boolean;
  reorderable?: boolean;
}

export interface DataTableColumnHeaderProps<K extends string = string> {
  column: DataTableHeaderColumn<K>;
  sortColumn?: K;
  sortDirection?: DataTableSortDirection;
  onSort?: (column: K) => void;
  filters?: DataTableFilterState;
  onFiltersChange?: (filters: DataTableFilterState) => void;
  filterLabels?: DataTableFilterLabels;
  labels?: DataTableLabels;
  /** Position among the reorderable columns — dnd-kit indexes within that set. */
  sortableIndex?: number;
  /** Position of the column being dragged, if any, in the same set. */
  activeSortableIndex?: number;
  onResizeStart?: (event: PointerEvent<HTMLElement>) => void;
  onResizeKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  onResizeReset?: () => void;
  /** Bumping this closes an open filter popover; see `FilterPopover`. */
  interactionToken?: number;
  /** Extra right padding, so the reset control never sits on a header label. */
  reserveEndSpace?: boolean;
  /** Rendered inside this cell — the reset control, on the last column. */
  children?: ReactNode;
}

/**
 * One `<th>`: label, sort button, filter popover, drag grip and resize handle.
 *
 * The grip is a real `<button>` carrying dnd-kit's handle ref rather than a
 * draggable `<th>`. dnd-kit only starts a drag from the handle when one is
 * given, so the sort button and the filter trigger keep their own clicks with
 * no activation-distance tuning, and the keyboard sensor binds to the grip for
 * free.
 */
export function DataTableColumnHeader<K extends string = string>(
  props: DataTableColumnHeaderProps<K>,
) {
  const { column, sortableIndex } = props;
  const reorderable =
    column.reorderable ?? (column.pinned == null && sortableIndex != null);
  return reorderable && sortableIndex != null ? (
    <SortableColumnHeader {...props} sortableIndex={sortableIndex} />
  ) : (
    <ColumnHeaderCell {...props} />
  );
}

function SortableColumnHeader<K extends string>({
  sortableIndex,
  ...props
}: DataTableColumnHeaderProps<K> & { sortableIndex: number }) {
  const { ref, handleRef, isDragSource, isDropTarget } = useSortable({
    id: props.column.id,
    index: sortableIndex,
    transition: null,
    plugins: SORTABLE_PLUGINS,
  });
  const { activeSortableIndex } = props;
  const dropSide =
    isDropTarget &&
    activeSortableIndex != null &&
    activeSortableIndex !== sortableIndex
      ? activeSortableIndex < sortableIndex
        ? 'end'
        : 'start'
      : undefined;

  return (
    <ColumnHeaderCell
      {...props}
      cellRef={ref}
      handleRef={handleRef}
      isDragSource={isDragSource}
      dropSide={dropSide}
    />
  );
}

function ColumnHeaderCell<K extends string>({
  column,
  sortColumn,
  sortDirection,
  onSort,
  filters,
  onFiltersChange,
  filterLabels,
  labels,
  onResizeStart,
  onResizeKeyDown,
  onResizeReset,
  interactionToken,
  reserveEndSpace,
  children,
  cellRef,
  handleRef,
  isDragSource,
  dropSide,
}: DataTableColumnHeaderProps<K> & {
  cellRef?: (element: Element | null) => void;
  handleRef?: (element: Element | null) => void;
  isDragSource?: boolean;
  dropSide?: 'start' | 'end';
}) {
  const labelText =
    column.labelText ?? (typeof column.label === 'string' ? column.label : '');
  const sortKey = onSort ? column.sortKey : undefined;
  const filterEnabled = Boolean(
    column.filter && filters && onFiltersChange && filterLabels,
  );
  const resizable = column.resizable ?? column.pinned == null;
  const truncate = column.truncate !== false;

  function setFilter(value?: DataTableFilterValue) {
    if (!filters || !onFiltersChange) return;
    const next = { ...filters };
    if (value) next[column.id] = value;
    else delete next[column.id];
    onFiltersChange(next);
  }

  return (
    <th
      ref={cellRef}
      scope="col"
      aria-sort={ariaSort(sortKey, sortColumn, sortDirection)}
      data-truncate={truncate ? undefined : 'off'}
      className={cn(
        'relative h-12 px-4 py-0 font-semibold',
        alignClass(column.align),
        reserveEndSpace && 'pr-9',
        isDragSource && 'bg-riso-blue-light',
      )}
    >
      <span
        className={cn(
          'flex h-8 items-center gap-1 leading-none',
          truncate && 'min-w-0',
          column.align === 'right' && 'justify-end',
          column.align === 'center' && 'justify-center',
        )}
      >
        {sortKey ? (
          <button
            type="button"
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-lg hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              truncate && 'min-w-0',
            )}
            onClick={() => onSort?.(sortKey)}
          >
            <span className={cn(truncate && 'truncate')}>{column.label}</span>
            <SortIcon
              column={sortKey}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
            />
          </button>
        ) : (
          <span className={cn(truncate && 'min-w-0 truncate')}>
            {column.label}
          </span>
        )}
        {filterEnabled && column.filter && filterLabels && filters && (
          <FilterPopover
            column={{ ...column, filter: column.filter }}
            value={filters[column.id]}
            labels={filterLabels}
            labelText={labelText}
            closeToken={interactionToken}
            onApply={(value) => setFilter(value)}
            onClear={() => setFilter()}
          />
        )}
      </span>

      {/* Out of the flow, in the cell's own left padding: in the header row a
          grip that reserves width clips the shortest labels to "Am…". */}
      {handleRef && labels && (
        <button
          ref={handleRef}
          type="button"
          aria-label={labels.reorderColumn(labelText)}
          className="absolute left-0.5 top-1/2 flex size-4 -translate-y-1/2 cursor-grab touch-none items-center justify-center rounded text-muted-foreground/60 opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing group-hover/head:opacity-100"
        >
          <GripVertical className="size-3.5" />
        </button>
      )}

      {dropSide && (
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-y-0 w-0.5 bg-riso-blue',
            dropSide === 'start' ? 'left-0' : 'right-0',
          )}
        />
      )}

      {resizable && onResizeStart && labels && (
        <span
          role="separator"
          aria-orientation="vertical"
          aria-label={labels.resizeColumn(labelText)}
          tabIndex={0}
          onPointerDown={onResizeStart}
          onKeyDown={onResizeKeyDown}
          onDoubleClick={onResizeReset}
          className="absolute inset-y-0 right-0 z-10 w-2 cursor-col-resize touch-none select-none after:absolute after:inset-y-2 after:left-1/2 after:w-0.5 after:-translate-x-1/2 after:rounded-full after:bg-transparent after:transition-colors hover:after:bg-riso-blue focus-visible:outline-none focus-visible:after:bg-riso-blue"
        />
      )}

      {children}
    </th>
  );
}

/** Shared with the cells in `data-table.tsx`, so a column lines up with itself. */
export function alignClass(align?: 'left' | 'right' | 'center') {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
}

function ariaSort<K extends string>(
  sortKey: K | undefined,
  sortColumn: K | undefined,
  sortDirection?: DataTableSortDirection,
) {
  if (!sortKey) return undefined;
  if (sortKey !== sortColumn) return 'none' as const;
  return sortDirection === 'asc'
    ? ('ascending' as const)
    : ('descending' as const);
}

function SortIcon({
  column,
  sortColumn,
  sortDirection,
}: {
  column: string;
  sortColumn?: string;
  sortDirection?: DataTableSortDirection;
}) {
  if (sortColumn !== column) {
    return (
      <ArrowUpDown className="size-3.5 shrink-0 text-muted-foreground/50" />
    );
  }
  return sortDirection === 'asc' ? (
    <ArrowUp className="size-3.5 shrink-0" />
  ) : (
    <ArrowDown className="size-3.5 shrink-0" />
  );
}
